/**
 * https://qiita.com/nqyutq/items/32fbcb56218594f4a341#%E3%83%88%E3%83%BC%E3%82%AF%E3%83%B3%E3%81%AE%E3%83%AA%E3%83%95%E3%83%AC%E3%83%83%E3%82%B7%E3%83%A5%E6%96%B9%E6%B3%95
 * https://www.bezkoder.com/vue-refresh-token/
 */

import Axios from 'axios'
import { Notify } from 'quasar'
import {gtm} from 'boot/gtmplugin'

const REFRESH_TOKEN_URL = '/swc-api/token/refresh/'
const MAX_REFRESH_RETRY_COUNT = 3
const REFRESH_RETRY_DELAY_MS = 1000
const REFRESH_RETRY_BACKOFF_FACTOR = 3
const TRANSIENT_REFRESH_ERROR_STATUSES = [502, 503, 504]

const ACCESS_TOKEN_KEY = 'auth/ACCESS_TOKEN'
const REFRESH_TOKEN_KEY = 'auth/REFRESH_TOKEN'
const REFRESH_LOCK_KEY = 'auth/REFRESH_LOCK'
const REFRESH_EVENT_KEY = 'auth/REFRESH_EVENT'
const REFRESH_CHANNEL_NAME = 'swc-auth-refresh'
const REFRESH_LOCK_TTL_MS = 10000
const REFRESH_LOCK_HEARTBEAT_MS = 2000
const REFRESH_WAIT_BUFFER_MS = 1000
const REFRESH_EVENT_SUCCESS = 'refresh-success'
const REFRESH_EVENT_FAILURE = 'refresh-failure'
const TAB_ID = `${Date.now()}-${Math.random().toString(36).slice(2)}`

const http = Axios.create({
    baseURL: '/',
    withCredentials: true,
    headers: {
        "Content-Type": "application/json; charset=UTF-8",
        "X-Requested-With": "XMLHttpRequest"
    }
})

let refreshTokenPromise
let refreshLockHeartbeatTimer = null
let loginRedirectTimer = null

const refreshChannel = typeof window !== 'undefined' && 'BroadcastChannel' in window
    ? new BroadcastChannel(REFRESH_CHANNEL_NAME)
    : null


const getStoredAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY)

const getStoredRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY)

const setStoredTokens = ({accessToken, refreshToken}) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
}

const clearStoredTokens = () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
}

const createRefreshEvent = (type, payload = {}) => ({
    type,
    payload,
    owner: TAB_ID,
    emittedAt: Date.now(),
    nonce: Math.random().toString(36).slice(2)
})

const emitRefreshEvent = (type, payload = {}) => {
    const event = createRefreshEvent(type, payload)
    localStorage.setItem(REFRESH_EVENT_KEY, JSON.stringify(event))
    if (refreshChannel) {
        refreshChannel.postMessage(event)
    }
}

const getRefreshLock = () => {
    const rawValue = localStorage.getItem(REFRESH_LOCK_KEY)
    if (!rawValue) {
        return null
    }
    try {
        return JSON.parse(rawValue)
    } catch (error) {
        localStorage.removeItem(REFRESH_LOCK_KEY)
        return null
    }
}

const isRefreshLockActive = (refreshLock) => {
    return refreshLock != null && Number(refreshLock.expiresAt) > Date.now()
}

const writeRefreshLock = (refreshLock) => {
    localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify(refreshLock))
}

const stopRefreshLockHeartbeat = () => {
    if (refreshLockHeartbeatTimer) {
        clearInterval(refreshLockHeartbeatTimer)
        refreshLockHeartbeatTimer = null
    }
}

const startRefreshLockHeartbeat = () => {
    stopRefreshLockHeartbeat()
    refreshLockHeartbeatTimer = setInterval(() => {
        const refreshLock = getRefreshLock()
        if (!refreshLock || refreshLock.owner !== TAB_ID) {
            stopRefreshLockHeartbeat()
            return
        }
        writeRefreshLock({
            owner: TAB_ID,
            expiresAt: Date.now() + REFRESH_LOCK_TTL_MS,
            heartbeatAt: Date.now()
        })
    }, REFRESH_LOCK_HEARTBEAT_MS)
}

const releaseRefreshLock = () => {
    const refreshLock = getRefreshLock()
    if (refreshLock && refreshLock.owner === TAB_ID) {
        localStorage.removeItem(REFRESH_LOCK_KEY)
    }
    stopRefreshLockHeartbeat()
}

const tryAcquireRefreshLock = () => {
    const currentRefreshLock = getRefreshLock()
    if (currentRefreshLock && currentRefreshLock.owner !== TAB_ID && isRefreshLockActive(currentRefreshLock)) {
        return false
    }

    writeRefreshLock({
        owner: TAB_ID,
        expiresAt: Date.now() + REFRESH_LOCK_TTL_MS,
        heartbeatAt: Date.now()
    })

    const confirmedRefreshLock = getRefreshLock()
    if (confirmedRefreshLock && confirmedRefreshLock.owner === TAB_ID) {
        startRefreshLockHeartbeat()
        return true
    }
    return false
}

const scheduleLoginRedirect = (message) => {
    if (message) {
        Notify.create({
            type: 'warning',
            message
        })
    }
    if (loginRedirectTimer) {
        return
    }
    loginRedirectTimer = setTimeout(() => {
        loginRedirectTimer = null
        if (location.pathname !== '/auth/login') {
            location.href = '/auth/login'
        }
    }, 5000)
}

const handleRefreshFailure = (message) => {
    clearStoredTokens()
    scheduleLoginRedirect(message)
}

const isRefreshUnauthorized = (error) => {
    return !!(error && error.response && error.response.status === 401)
}

const applyRefreshEvent = (refreshEvent) => {
    if (!refreshEvent || refreshEvent.owner === TAB_ID) {
        return
    }
    if (refreshEvent.type === REFRESH_EVENT_SUCCESS) {
        setStoredTokens(refreshEvent.payload)
        return
    }
    if (refreshEvent.type === REFRESH_EVENT_FAILURE) {
        handleRefreshFailure(refreshEvent.payload.message)
    }
}

const waitForRefreshOutcome = (waitMs) => {
    return new Promise((resolve, reject) => {
        let settled = false
        let timerId = null

        const cleanup = () => {
            if (timerId) {
                clearTimeout(timerId)
            }
            if (refreshChannel) {
                refreshChannel.removeEventListener('message', onChannelMessage)
            }
            if (typeof window !== 'undefined') {
                window.removeEventListener('storage', onStorageEvent)
            }
        }

        const settleWithResolve = (value) => {
            if (settled) {
                return
            }
            settled = true
            cleanup()
            resolve(value)
        }

        const settleWithReject = (error) => {
            if (settled) {
                return
            }
            settled = true
            cleanup()
            reject(error)
        }

        const processRefreshEvent = (refreshEvent) => {
            if (!refreshEvent) {
                return
            }
            if (refreshEvent.type === REFRESH_EVENT_SUCCESS) {
                setStoredTokens(refreshEvent.payload)
                settleWithResolve(refreshEvent.payload)
                return
            }
            if (refreshEvent.type === REFRESH_EVENT_FAILURE) {
                handleRefreshFailure(refreshEvent.payload.message)
                settleWithReject(new Error(refreshEvent.payload.message || 'refresh failed'))
            }
        }

        const onChannelMessage = (event) => {
            processRefreshEvent(event.data)
        }

        const onStorageEvent = (event) => {
            if (event.key !== REFRESH_EVENT_KEY || !event.newValue) {
                return
            }
            try {
                processRefreshEvent(JSON.parse(event.newValue))
            } catch (error) {
                settleWithReject(error)
            }
        }

        if (refreshChannel) {
            refreshChannel.addEventListener('message', onChannelMessage)
        }
        if (typeof window !== 'undefined') {
            window.addEventListener('storage', onStorageEvent)
        }

        timerId = setTimeout(() => {
            settleWithResolve(null)
        }, waitMs)
    })
}

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms))

// リトライ間隔は 1秒 → 3秒 → 9秒 と指数的に広げ、一時的なサーバー輻輳を跨げるようにする
const getRefreshRetryDelayMs = (retryCount) => {
    return REFRESH_RETRY_DELAY_MS * Math.pow(REFRESH_RETRY_BACKOFF_FACTOR, retryCount)
}

const isRefreshRequest = (config) => config?.url === REFRESH_TOKEN_URL

const isTransientRefreshError = (error) => {
    const status = error?.response?.status
    return !error?.response || TRANSIENT_REFRESH_ERROR_STATUSES.includes(status)
}

const getErrorStatus = (error) => error?.response?.status || 'network-error'

const refreshRetryCountOf = (error) => error?.config?._refreshRetryCount || 0

const logRefreshEvent = (swacEvent, error, retryCount, retryDelayMs = null) => {
    gtm.logEvent(swacEvent, {
        auth_refresh_status: `${getErrorStatus(error)}`,
        auth_refresh_retry_count: retryCount,
        auth_refresh_retry_delay_ms: retryDelayMs,
        auth_refresh_transient: isTransientRefreshError(error),
    })
}

const getRefreshToken = () => {
    return http.post(
        REFRESH_TOKEN_URL,
        {refresh: getStoredRefreshToken()},
        {
            withCredentials: true,
            // headers: { 'X-CSRF-TOKEN': Vue.$cookies.get('csrf_refresh_token') }
        }
    ).then((res) => ({accessToken: res.data.access, refreshToken: res.data.refresh}))
}

const runRefreshRequest = () => {
    return getRefreshToken()
        .then((token) => {
            setStoredTokens(token)
            emitRefreshEvent(REFRESH_EVENT_SUCCESS, token)
            releaseRefreshLock()
            return token
        })
        .catch((error) => {
            releaseRefreshLock()
            if (isRefreshUnauthorized(error)) {
                const message = `セッション情報の有効期限が切れました。再度ログインしてください。 : ${error}`
                emitRefreshEvent(REFRESH_EVENT_FAILURE, {message})
                handleRefreshFailure(message)
            } else {
                // リトライを尽くしても復旧しないため、ログイン画面へ誘導する。
                // 一時的な障害の可能性があり他タブは継続できることがあるため、系列失効の通知は行わない。
                const message = 'セッションの更新に失敗しました。通信状態を確認して、再度ログインしてください。'
                logRefreshEvent('auth_refresh_logout_scheduled', error, refreshRetryCountOf(error))
                handleRefreshFailure(message)
            }
            throw error
        })
}

const coordinateRefresh = async () => {
    while (true) {
        if (tryAcquireRefreshLock()) {
            return runRefreshRequest()
        }

        const refreshLock = getRefreshLock()
        const waitMs = refreshLock && isRefreshLockActive(refreshLock)
            ? Math.max(Number(refreshLock.expiresAt) - Date.now(), 0) + REFRESH_WAIT_BUFFER_MS
            : REFRESH_WAIT_BUFFER_MS
        const token = await waitForRefreshOutcome(waitMs)
        if (token) {
            return token
        }
    }
}

const getCoordinatedRefreshToken = () => {
    if (!refreshTokenPromise) {
        refreshTokenPromise = coordinateRefresh().finally(() => {
            refreshTokenPromise = null
        })
    }
    return refreshTokenPromise
}

if (refreshChannel) {
    refreshChannel.addEventListener('message', (event) => {
        applyRefreshEvent(event.data)
    })
}

if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
        if (event.key !== REFRESH_EVENT_KEY || !event.newValue) {
            return
        }
        try {
            applyRefreshEvent(JSON.parse(event.newValue))
        } catch (error) {
            console.error('Failed to apply refresh event.', error)
        }
    })
}

http.interceptors.response.use(
    config => {
        const token = getStoredAccessToken()
        config.headers['Authorization'] = 'Bearer ' + token
        return config
    },
    error => {
        if (isRefreshRequest(error.config)) {
            const refreshRetryCount = error.config._refreshRetryCount || 0

            if (isTransientRefreshError(error) && refreshRetryCount < MAX_REFRESH_RETRY_COUNT) {
                const retryDelayMs = getRefreshRetryDelayMs(refreshRetryCount)
                error.config._refreshRetryCount = refreshRetryCount + 1
                logRefreshEvent('auth_refresh_retry_scheduled', error, error.config._refreshRetryCount, retryDelayMs)
                return wait(retryDelayMs)
                    .then(() => http.request(error.config))
                    .then((response) => {
                        gtm.logEvent('auth_refresh_retry_succeeded', {
                            auth_refresh_retry_count: error.config._refreshRetryCount,
                        })
                        return response
                    })
            }

            return Promise.reject(error)
        }
        if (error.config && error.response && error.response.status === 401 && !error.config._retry) {
            error.config._retry = true

            return getCoordinatedRefreshToken().then(token => {
                // error.config.headers['X-CSRF-TOKEN'] = token
                const {accessToken, refreshToken} = token
                error.config.headers['Authorization'] = 'Bearer ' + accessToken
                return http.request(error.config)
            })
        } else {
            Notify.create({
                type: "warning",
                message: `Server Response Error : ${error}`
              })
        }
        return Promise.reject(error)
    }
)

export default http
